/**
 * Utility for Quick!Bible publication dates and relative time formatting.
 * Displays date in Asia/Manila timezone + relative time.
 * Output example: "Nov 5, 2026 • 5 mins ago", "Nov 5, 2026 • Just now", "Nov 4, 2026 • 1 day ago"
 */
export function formatPostTimestamp(isoOrDate: string | Date | undefined | null): string {
  if (!isoOrDate) return 'Just now';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (isNaN(d.getTime())) return 'Just now';

  // 1. Format date using Asia/Manila timezone
  let dateStr = '';
  try {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Manila',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    dateStr = dateFormatter.format(d);
  } catch {
    dateStr = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  // 2. Relative time calculation
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSecs = Math.max(0, Math.floor(diffMs / 1000));
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  let relativeStr = 'Just now';
  if (diffMins < 1) {
    relativeStr = 'Just now';
  } else if (diffMins < 60) {
    relativeStr = `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    relativeStr = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    relativeStr = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffWeeks < 4) {
    relativeStr = `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  } else {
    relativeStr = `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  }

  return `${dateStr} • ${relativeStr}`;
}
